import { FC, ReactNode } from 'react';
import { Transition } from '@headlessui/react';
import { ArrowNarrowLeftIcon } from '@heroicons/react/outline';

import PageContainer from '../../../components/Containers/PageContainer';
import IconButton from '../../../components/IconButton';
import UnsavedChangesModal from '../../../components/Modals/UnsavedChangesModal';
import { defaultTransitionProps } from '../../../lib/animation';
import Spinner from '../../../svgs/Spinner';

interface Props {
  show: boolean;
  title: string;
  onBack: () => void;
  /** Right side of the fixed header (save button, spinner, ...). */
  headerActions: ReactNode;
  isUnsavedChangesModalOpen: boolean;
  onCloseUnsavedChangesModal: () => void;
  onConfirmDiscard: () => void;
  children: ReactNode;
}

/** Full-page template editor layout: fixed header + page body + unsaved-changes modal. */
const TemplateEditorShell: FC<Props> = ({
  show,
  title,
  onBack,
  headerActions,
  isUnsavedChangesModalOpen,
  onCloseUnsavedChangesModal,
  onConfirmDiscard,
  children,
}) => (
  <>
    <div className="relative scroll-smooth">
      <Transition show={show} {...defaultTransitionProps}>
        <div className="t-16 fixed z-10 flex w-full flex-row items-center justify-between border-b border-neutral-50 bg-white py-3 px-20">
          <div className="w-[289px]">
            <IconButton
              IconComponent={ArrowNarrowLeftIcon}
              iconClassName="text-secondary-100 w-5"
              aria-label="Back"
              className="rounded-xl px-3 py-2"
              onClick={onBack}
            >
              <div className="small-caption ml-2 font-bold text-secondary-100">
                Back
              </div>
            </IconButton>
          </div>
          <span className="text-category font-medium text-green-150 line-clamp-1">
            {title}
          </span>
          <div className="flex min-w-[289px] flex-row items-center justify-end gap-3">
            {headerActions}
          </div>
        </div>
      </Transition>

      <PageContainer
        noPadding
        containerClassName="bg-white min-h-[calc(100vh-48px)] h-auto items-center pt-24 pb-16"
      >
        <Transition
          show={show}
          {...defaultTransitionProps}
          beforeEnter={() => {
            window.scrollTo(0, 0);
          }}
        >
          {!show ? (
            <div className="flex justify-center py-20">
              <Spinner className="h-8 w-8 text-neutral-125" />
            </div>
          ) : (
            children
          )}
        </Transition>
      </PageContainer>
    </div>

    <UnsavedChangesModal
      isModalOpen={isUnsavedChangesModalOpen}
      setClosed={onCloseUnsavedChangesModal}
      onConfirm={onConfirmDiscard}
    />
  </>
);

export default TemplateEditorShell;
